/**
 * SettingsPage Component
 * 
 * Application settings and management.
 * Built with shadcn components.
 */

import { useState, useEffect } from 'react'
import { Sun, Moon, Save } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { ApiServices } from '../../services/ApiServices'
import { useAuth } from '../../context/AuthContext'
import UserManagement from './UserManagement'
import AuditLogsDashboard from './AuditLogsDashboard'

const SettingsPage = ({ mode, toggleColorMode }) => {
  const { user } = useAuth();

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'settings');
  }, []);

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [hasUserPermission, setHasUserPermission] = useState(false);

  // Load users (real logic)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await ApiServices.getUsers();
        if (response && response.users) {
          setHasUserPermission(true);
        } else {
          // If no users returned or permission error caught in ApiServices
          setHasUserPermission(false);
        }
      } catch (error) {
        // Permission denied is expected for regular employees
        setHasUserPermission(false);
      }
    };

    // Only fetch if likely to have permissions (optional optimization), 
    // but API call is safe as it handles permissions.
    fetchUsers();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords don't match!");
      return;
    }

    try {
      await ApiServices.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      alert('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert(`Failed to change password: ${error.message}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Настройки</h1>
        <p className="text-muted-foreground mt-2">
          Управление настройками приложения и учетной записи.
        </p>
      </div>

      {/* Theme Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Внешний вид</CardTitle>
          <CardDescription>
            Настройка внешнего вида приложения.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border">
            <div className="space-y-1">
              <h3 className="font-medium">Режим темы</h3>
              <p className="text-sm text-muted-foreground">
                Переключение между светлой и темной темами.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium capitalize min-w-[3rem] text-right">
                {mode}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleColorMode}
                className="rounded-full"
                aria-label="Toggle theme"
              >
                {mode === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Настройки профиля</CardTitle>
          <CardDescription>
            Управление личной информацией и безопасностью.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-lg">
              <h3 className="font-medium mb-4">Смена пароля</h3>
              <div className="space-y-2">
                <Label htmlFor="current-password">Текущий пароль</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Новый пароль</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Подтвердите новый пароль</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full mt-2" disabled={!passwordData.currentPassword}>
                <Save className="mr-2 h-4 w-4" />
                Обновить пароль
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      {hasUserPermission && (
        <>
          <UserManagement />
          <AuditLogsDashboard />
        </>
      )}
    </div>
  )
}

export default SettingsPage